import React, { useState, Component } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import Wizard from '../components/Wizard';
import { ShoppingBag, Shirt, Home, Zap, Bot, Share2, Database, ShoppingCart, Mail, Search, BarChart3, LineChart, CheckIcon, TrendingUpIcon, ArrowUpRight, ChevronRight, ArrowRightIcon, BarChart2Icon, PieChartIcon, ActivityIcon, LayoutDashboardIcon, CreditCardIcon, BoxIcon, TagIcon, RefreshCwIcon } from 'lucide-react';
// Reusable Components
function ValueCard({
  Icon,
  title,
  text
}) {
  return <div className="bg-white rounded-xl p-6 shadow-card border border-gray-100 flex flex-col items-center text-center gap-4" data-id="element-2823">
      <Icon className="h-12 w-12 text-primary" data-id="element-2824" />
      <h3 className="font-semibold text-lg" data-id="element-2825">{title}</h3>
      <p className="text-gray-600" data-id="element-2826">{text}</p>
    </div>;
}
function Feature({
  icon: Icon,
  text
}) {
  return <div className="flex gap-3 items-start" data-id="element-2827">
      <Icon className="h-6 w-6 text-primary mt-1" data-id="element-2828" />
      <p className="text-gray-700 leading-relaxed" data-id="element-2829">{text}</p>
    </div>;
}
function KeyStat({
  label,
  value
}) {
  return <div className="rounded-xl border border-gray-200 p-4 bg-white" data-id="element-2830">
      <div className="text-xs uppercase tracking-wide text-gray-500" data-id="element-2831">
        {label}
      </div>
      <div className="text-2xl font-bold mt-1" data-id="element-2832">{value}</div>
    </div>;
}
function ChatWindow({
  title,
  subtitle,
  transcript
}) {
  return <div className="rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col" data-id="element-2833">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50" data-id="element-2834">
        <div className="text-sm font-semibold" data-id="element-2835">{title}</div>
        <div className="text-xs text-gray-500" data-id="element-2836">{subtitle}</div>
      </div>
      <div className="p-3 sm:p-4 space-y-3 bg-white" data-id="element-2837">
        {transcript.messages.map((m, i) => <ChatMessage key={i} m={m} data-id="element-2838" />)}
      </div>
      {/* Composer mock */}
      <div className="border-t border-gray-200 bg-gray-50 px-3 py-2" data-id="element-2839">
        <div className="flex items-center gap-2" data-id="element-2840">
          <div className="flex-1" data-id="element-2841">
            <div className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-xs text-gray-500" data-id="element-2842">
              Type a message…
            </div>
          </div>
          <Button variant="primary" size="sm" data-id="element-2843">
            Send
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-2" data-id="element-2844">
          {transcript.suggestions.map((s, i) => <button key={i} className="text-xs px-2 py-1 rounded-full border border-gray-300 hover:bg-gray-100" data-id="element-2845">
              {s}
            </button>)}
        </div>
      </div>
    </div>;
}
function ChatMessage({
  m
}) {
  const isUser = m.role === 'user';
  return <div className={'flex gap-3 ' + (isUser ? 'flex-row-reverse' : '')} data-id="element-2846">
      {/* Avatar */}
      <div className="shrink-0 h-8 w-8 rounded-md grid place-items-center border border-gray-200 bg-gray-50" data-id="element-2847">
        {isUser ? <div className="text-xs font-semibold" data-id="element-2848">You</div> : <div className="text-xs font-semibold" data-id="element-2849">GPT</div>}
      </div>
      {/* Bubble */}
      <div className={(isUser ? 'bg-primary text-white' : 'bg-gray-50 text-gray-800') + ' max-w-[100%] rounded-2xl px-3 py-2 text-sm leading-relaxed border ' + (isUser ? 'border-primary' : 'border-gray-200')} data-id="element-2850">
        {m.type === 'tool' ? <ToolBlock tool={m.tool} data-id="element-2851" /> : <div className="space-y-2" data-id="element-2852">
            <div data-id="element-2853">{m.content}</div>
            {m.sources && m.sources.length > 0 && <div className="flex flex-wrap gap-1 mt-2" data-id="element-2854">
                {m.sources.map((s, i) => <span key={i} className="inline-flex items-center text-xs px-2 py-0.5 rounded-full border border-gray-300 bg-white text-gray-700" data-id="element-2855">
                    Source {i + 1}
                  </span>)}
              </div>}
            {m.products && m.products.length > 0 && <div className="grid grid-cols-1 gap-2 mt-2" data-id="element-2856">
                {m.products.map((p, i) => <ProductCard key={i} p={p} data-id="element-2857" />)}
              </div>}
          </div>}
      </div>
    </div>;
}
function ToolBlock({
  tool
}) {
  if (tool?.kind === 'search') {
    return <div className="bg-white border border-gray-200 rounded-xl p-3 text-gray-700" data-id="element-2858">
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-2" data-id="element-2859">
          Searching the web…
        </div>
        <ul className="list-disc pl-5 text-xs space-y-1" data-id="element-2860">
          {tool.items.map((t, i) => <li key={i} className="truncate" data-id="element-2861">
              <span className="font-medium" data-id="element-2862">{t.title}</span> —{' '}
              <span className="text-gray-500" data-id="element-2863">{t.domain}</span>
            </li>)}
        </ul>
      </div>;
  }
  return null;
}
function ProductCard({
  p
}) {
  return <div className="rounded-xl border border-gray-200 bg-white overflow-hidden" data-id="element-2864">
      <div className="p-3 grid grid-cols-[72px_1fr] gap-3 items-center" data-id="element-2865">
        <div className="h-16 w-16 rounded-lg bg-gray-100 border border-gray-200 overflow-hidden" data-id="element-2866">
          <img src={p.image} alt={p.name} className="h-full w-full object-cover" data-id="element-2867" />
        </div>
        <div data-id="element-2868">
          <div className="text-sm font-semibold text-gray-800" data-id="element-2869">{p.name}</div>
          <div className="text-xs text-gray-600" data-id="element-2870">{p.subtitle}</div>
          <div className="flex items-center gap-2 mt-1" data-id="element-2871">
            <span className="text-xs font-semibold" data-id="element-2872">{p.price}</span>
            {p.badges?.map((b, i) => <span key={i} className="text-xs px-2 py-0.5 rounded-full border border-gray-300 bg-gray-50" data-id="element-2873">
                {b}
              </span>)}
          </div>
          <div className="mt-2 flex gap-2" data-id="element-2874">
            <Button variant="primary" size="sm" className="text-xs" data-id="element-2875">
              Add to Cart
            </Button>
            <Button variant="outline" size="sm" className="text-xs" data-id="element-2876">
              Compare
            </Button>
          </div>
        </div>
      </div>
    </div>;
}
function InfoTile({
  icon: Icon,
  title,
  items
}) {
  return <div className="bg-white rounded-xl p-6 shadow-card border border-gray-100" data-id="element-2877">
      <div className="flex items-center gap-2 mb-4" data-id="element-2878">
        <Icon className="h-5 w-5 text-primary" data-id="element-2879" />
        <span className="font-semibold" data-id="element-2880">{title}</span>
      </div>
      <ul className="space-y-2" data-id="element-2881">
        {items.map((item, index) => <li key={index} className="flex gap-2 text-sm text-gray-600" data-id="element-2882">
            <CheckIcon className="h-5 w-5 text-primary flex-shrink-0" data-id="element-2883" />
            <span data-id="element-2884">{item}</span>
          </li>)}
      </ul>
    </div>;
}
// Demo data
const tvTranscript = {
  messages: [{
    role: 'user',
    type: 'text',
    content: 'I need a 65" TV for a bright living room under $1,000. I watch sports and Netflix.'
  }, {
    role: 'assistant',
    type: 'text',
    content: 'Got it. For bright rooms, look for high peak brightness (≥ 700 nits) and strong anti‑reflective coating. Prioritize 120Hz for sports. Here are 3 options that match your budget:',
    products: [{
      name: 'Hisense U7K 65" Mini‑LED',
      subtitle: '120Hz • ~1000 nits • Dolby Vision',
      price: '$799',
      image: 'https://images.unsplash.com/photo-1593784991095-a205069470b6?q=80&w=2070&auto=format&fit=crop',
      badges: ['Best Value']
    }, {
      name: 'TCL QM8 65"',
      subtitle: 'Mini‑LED • Very bright • Low reflections',
      price: '$999',
      image: 'https://images.unsplash.com/photo-1540574163026-643ea20ade25?q=80&w=600&auto=format&fit=crop',
      badges: ['Bright Rooms']
    }, {
      name: 'Samsung Q80C 65"',
      subtitle: 'QLED • Great motion • Tizen',
      price: '$949',
      image: 'https://images.unsplash.com/photo-1601944179066-29b8f7e29c3a?q=80&w=2070&auto=format&fit=crop'
    }]
  }, {
    role: 'user',
    type: 'text',
    content: 'Which one has the best motion for sports?'
  }, {
    role: 'assistant',
    type: 'text',
    content: 'Samsung Q80C has excellent motion handling and 120Hz for smooth sports playback. If you want the brightest screen though, TCL QM8 wins. Want me to add the Samsung to cart?'
  }],
  suggestions: ['Compare brightness vs. reflections', 'Show wall‑mount options', 'Find matching soundbars']
};
const fashionTranscript = {
  messages: [{
    role: 'user',
    type: 'text',
    content: 'Outfit for a summer rooftop event, smart‑casual, under $250. EU 42 shoes.'
  }, {
    role: 'assistant',
    type: 'text',
    content: "Here's a polished, breathable combo that fits the budget:",
    products: [{
      name: 'Linen‑blend Oxford Shirt',
      subtitle: 'Slim fit • Off‑white',
      price: '$49',
      image: 'https://images.unsplash.com/photo-1541099649105-f69ad21f3246?q=80&w=600&auto=format&fit=crop',
      badges: ['Breathable']
    }, {
      name: 'Chino Trousers',
      subtitle: 'Tapered • Sand',
      price: '$59',
      image: 'https://images.unsplash.com/photo-1624378439575-d8705ad7ae80?q=80&w=2070&auto=format&fit=crop'
    }, {
      name: 'Minimal Leather Sneakers (EU 42)',
      subtitle: 'Smart‑casual • White',
      price: '$119',
      image: 'https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=600&auto=format&fit=crop',
      badges: ['True to size']
    }]
  }, {
    role: 'user',
    type: 'text',
    content: 'Swap the trousers for navy and keep total under $250.'
  }, {
    role: 'assistant',
    type: 'text',
    content: 'Done. Navy chinos are $62 — new total $230. Add to cart?'
  }],
  suggestions: ['Try with blazer', 'Show weather‑friendly fabrics', 'Suggest accessories']
};
const dwTranscript = {
  messages: [{
    role: 'user',
    type: 'text',
    content: 'Need a quiet 24" dishwasher with a 3rd rack under $700.'
  }, {
    role: 'assistant',
    type: 'text',
    content: 'Look for ≤ 44 dBA for quiet operation and a dedicated 3rd rack. These fit:',
    products: [{
      name: 'Bosch 300 Series (SHEM63)',
      subtitle: '44 dBA • 3rd rack • Stainless',
      price: '$699',
      image: 'https://images.unsplash.com/photo-1584269600464-37b1b58a9fe7?q=80&w=2071&auto=format&fit=crop',
      badges: ['Quiet']
    }, {
      name: 'LG QuadWash',
      subtitle: 'Dynamic jets • 3rd rack',
      price: '$649',
      image: 'https://images.unsplash.com/photo-1584269600519-42d7b7c67e1c?q=80&w=2071&auto=format&fit=crop'
    }]
  }, {
    role: 'user',
    type: 'text',
    content: 'Which is more reliable long‑term?'
  }, {
    role: 'assistant',
    type: 'text',
    content: 'Bosch has a strong reliability record and parts support; LG is feature‑rich. Want Bosch added to cart and installation options?'
  }],
  suggestions: ['Show panel‑ready models', 'Compare noise (dBA)', 'See delivery windows']
};
// Sample data for the hockey stick chart
const searchTrendData = [{
  month: 'Jan',
  searches: 100
}, {
  month: 'Feb',
  searches: 120
}, {
  month: 'Mar',
  searches: 150
}, {
  month: 'Apr',
  searches: 200
}, {
  month: 'May',
  searches: 300
}, {
  month: 'Jun',
  searches: 500
}, {
  month: 'Jul',
  searches: 900
}, {
  month: 'Aug',
  searches: 1500
}];
export default function GPTStorePitchPage() {
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  return <div className="min-h-screen bg-white text-gray-900" data-id="element-2885">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-slate-900 to-slate-700 text-white py-20" data-id="element-2886">
        <div className="max-w-6xl mx-auto px-6 text-center" data-id="element-2887">
          <h1 className="text-4xl sm:text-6xl font-bold tracking-tight mb-6" data-id="element-2888">
            Bring Your Store Into the GPT Era
          </h1>
          <p className="mt-6 text-lg max-w-3xl mx-auto text-slate-200" data-id="element-2889">
            Shoppers are searching with AI. Don't get left behind. Shopilots
            helps electronic shops, fashion designers, and home appliance
            retailers launch their{' '}
            <span className="font-semibold" data-id="element-2890">own GPT Store</span> — in just
            hours.
          </p>
          <div className="mt-8 flex justify-center" data-id="element-2891">
            <Button variant="primary" size="lg" className="bg-white text-slate-900 hover:bg-slate-200" onClick={() => setIsWizardOpen(true)} data-id="element-2892">
              Get Your GPT Store →
            </Button>
          </div>
        </div>
      </section>

      {/* Trending Section - NEW */}
      <section className="py-16 bg-gradient-to-r from-purple-900 to-indigo-900 text-white" data-id="element-2893">
        <div className="max-w-6xl mx-auto px-6" data-id="element-2894">
          <div className="grid md:grid-cols-2 gap-10 items-center" data-id="element-2895">
            <div data-id="element-2896">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-purple-700 text-xs font-medium mb-4" data-id="element-2897">
                <TrendingUpIcon className="h-3.5 w-3.5 mr-1" data-id="element-2898" />
                Trending on Google
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold mb-6" data-id="element-2899">
                Lead the Way or Drop Dead
              </h2>
              <p className="text-xl mb-8" data-id="element-2900">
                GPT store searches on Google are trending exponentially in North
                America. If you're leading, you better keep it. If you're not,
                it's time to own it.
              </p>
              <Button variant="primary" size="lg" className="bg-white text-indigo-900 hover:bg-gray-100" onClick={() => setIsWizardOpen(true)} data-id="element-2901">
                Sign Up to Try Our Proprietary Agentic AI Store
              </Button>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20" data-id="element-2902">
              <h3 className="text-xl font-semibold mb-4" data-id="element-2903">
                GPT Store Search Trend
              </h3>
              <div className="h-64 relative" data-id="element-2904">
                {/* Line chart visualization */}
                <div className="absolute inset-0" data-id="element-2905">
                  <svg viewBox="0 0 100 70" className="w-full h-full" data-id="element-2906">
                    {/* Grid lines */}
                    <line x1="0" y1="70" x2="100" y2="70" stroke="rgba(255,255,255,0.1)" data-id="element-2907" />
                    <line x1="0" y1="52.5" x2="100" y2="52.5" stroke="rgba(255,255,255,0.1)" data-id="element-2908" />
                    <line x1="0" y1="35" x2="100" y2="35" stroke="rgba(255,255,255,0.1)" data-id="element-2909" />
                    <line x1="0" y1="17.5" x2="100" y2="17.5" stroke="rgba(255,255,255,0.1)" data-id="element-2910" />
                    <line x1="0" y1="0" x2="100" y2="0" stroke="rgba(255,255,255,0.1)" data-id="element-2911" />
                    {/* Line chart path */}
                    <path d="M 0,70 L 12.5,68 L 25,65 L 37.5,60 L 50,50 L 62.5,35 L 75,15 L 87.5,5 L 100,0" fill="none" stroke="#a855f7" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-2912" />
                    {/* Gradient area under the line */}
                    <path d="M 0,70 L 12.5,68 L 25,65 L 37.5,60 L 50,50 L 62.5,35 L 75,15 L 87.5,5 L 100,0 L 100,70 L 0,70" fill="url(#gradient)" opacity="0.5" data-id="element-2913" />
                    {/* Gradient definition */}
                    <defs data-id="element-2914">
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-2915">
                        <stop offset="0%" stopColor="#a855f7" stopOpacity="0.8" data-id="element-2916" />
                        <stop offset="100%" stopColor="#a855f7" stopOpacity="0.1" data-id="element-2917" />
                      </linearGradient>
                    </defs>
                    {/* Data points */}
                    <circle cx="0" cy="70" r="2" fill="#a855f7" data-id="element-2918" />
                    <circle cx="12.5" cy="68" r="2" fill="#a855f7" data-id="element-2919" />
                    <circle cx="25" cy="65" r="2" fill="#a855f7" data-id="element-2920" />
                    <circle cx="37.5" cy="60" r="2" fill="#a855f7" data-id="element-2921" />
                    <circle cx="50" cy="50" r="2" fill="#a855f7" data-id="element-2922" />
                    <circle cx="62.5" cy="35" r="2" fill="#a855f7" data-id="element-2923" />
                    <circle cx="75" cy="15" r="2" fill="#a855f7" data-id="element-2924" />
                    <circle cx="87.5" cy="5" r="2" fill="#a855f7" data-id="element-2925" />
                    <circle cx="100" cy="0" r="4" fill="#a855f7" stroke="white" strokeWidth="1" data-id="element-2926" />
                  </svg>
                </div>
                {/* Bar chart visualization */}
                <div className="absolute inset-0 flex items-end" data-id="element-2927">
                  {searchTrendData.map((point, index) => <div key={index} className="flex-1 flex flex-col items-center" data-id="element-2928">
                      <div className="w-full bg-gradient-to-t from-purple-500 to-indigo-400 rounded-t-sm" style={{
                    height: `${point.searches / 1500 * 100}%`,
                    maxWidth: '30px',
                    margin: '0 auto'
                  }} data-id="element-2929"></div>
                      <div className="text-xs mt-2 text-white/70" data-id="element-2930">
                        {point.month}
                      </div>
                    </div>)}
                </div>
                {/* Exponential growth indicator */}
                <div className="absolute top-4 right-4 flex items-center bg-white/20 rounded-full px-3 py-1" data-id="element-2931">
                  <ArrowUpRight className="h-4 w-4 text-green-400 mr-1" data-id="element-2932" />
                  <span className="text-xs font-medium text-green-400" data-id="element-2933">
                    +1400%
                  </span>
                </div>
              </div>
              <div className="text-center mt-4 text-sm text-white/70" data-id="element-2934">
                Search volume for "GPT Stores" in North America (Last 8 months)
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Now */}
      <section className="py-16 bg-white" data-id="element-2935">
        <div className="max-w-6xl mx-auto px-6" data-id="element-2936">
          <h2 className="text-3xl font-bold text-center mb-12" data-id="element-2937">
            Why Open Your Own GPT Store?
          </h2>
          <div className="grid sm:grid-cols-3 gap-6" data-id="element-2938">
            <ValueCard Icon={ShoppingBag} title="Electronics Retailers" text="Turn complex specs (TVs, laptops, phones) into simple AI‑guided conversations. Customers find the right model faster — and buy sooner." data-id="element-2939" />
            <ValueCard Icon={Shirt} title="Fashion Designers" text="Offer personal styling through GPT. Match collections to customers' tastes, budget, and upcoming events — just like a personal shopper." data-id="element-2940" />
            <ValueCard Icon={Home} title="Home Appliance Stores" text="From refrigerators to washing machines, customers want guidance. GPT handles the research for them, building trust and closing sales." data-id="element-2941" />
          </div>
        </div>
      </section>

      {/* Why Shopilots */}
      <section className="py-16 bg-gray-50 border-t border-b border-gray-200" data-id="element-2942">
        <div className="max-w-6xl mx-auto px-6" data-id="element-2943">
          <h2 className="text-3xl font-bold text-center mb-12" data-id="element-2944">
            Why Shopilots?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-2945">
            <div className="space-y-6" data-id="element-2946">
              <Feature icon={Zap} text={<>
                    <strong data-id="element-2948">Real‑Time Inventory:</strong> Our API connects
                    directly to your catalog. Prices, stock, and promotions are
                    always up‑to‑date — no manual updates needed.
                  </>} data-id="element-2947" />
              <Feature icon={Bot} text={<>
                    <strong data-id="element-2950">Conversational Shopping:</strong> Customers ask
                    questions, compare products, and get personalized advice,
                    just like talking to your best sales associate.
                  </>} data-id="element-2949" />
              <Feature icon={Share2} text={<>
                    <strong data-id="element-2952">Multi‑Channel Reach:</strong> Deploy the same GPT
                    Store across your website, WhatsApp, Messenger, or embed
                    directly into ChatGPT.
                  </>} data-id="element-2951" />
              <Feature icon={Database} text={<>
                    <strong data-id="element-2954">"Your data is here":</strong> We map your structured
                    data (attributes, variants, images, policies) into an
                    AI‑ready index so your GPT always answers with facts from{' '}
                    <em data-id="element-2955">your</em> catalog.
                  </>} data-id="element-2953" />
            </div>
            <div data-id="element-2956">
              <div className="rounded-xl shadow-card border border-gray-200 overflow-hidden bg-white" data-id="element-2957">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center gap-2" data-id="element-2958">
                  <Database className="h-5 w-5 text-gray-700" data-id="element-2959" />
                  <span className="text-sm font-semibold" data-id="element-2960">
                    Shopilots API — Live Inventory
                  </span>
                </div>
                <div className="p-6 grid sm:grid-cols-2 gap-4" data-id="element-2961">
                  <KeyStat label="SKUs Synced" value="42,187" data-id="element-2962" />
                  <KeyStat label="Avg. Latency" value="92ms" data-id="element-2963" />
                  <KeyStat label="Price Changes / hr" value="318" data-id="element-2964" />
                  <KeyStat label="Stock Events / hr" value="1,204" data-id="element-2965" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3" data-id="element-2966">
                Optimized for retrieval‑augmented chat flows and verified
                answers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Showcase: Hyper‑Realistic ChatGPT‑like Threads */}
      <section className="py-16 bg-white" data-id="element-2967">
        <div className="max-w-6xl mx-auto px-6" data-id="element-2968">
          <h2 className="text-3xl font-bold text-center mb-6" data-id="element-2969">
            See It: Real Conversations That Convert
          </h2>
          <p className="text-center text-gray-600 max-w-3xl mx-auto mb-10" data-id="element-2970">
            Three live‑feeling demos rendered below — styled like ChatGPT. Each
            use‑case shows intents, reasoning, and product cards with
            add‑to‑cart handoff.
          </p>
          <div className="grid lg:grid-cols-3 gap-6 items-start" data-id="element-2971">
            <ChatWindow title="Consumer Electronics (TV)" subtitle='Find a 65" TV for a bright room under $1,000' transcript={tvTranscript} data-id="element-2972" />
            <ChatWindow title="Fashion Styling" subtitle="Smart‑casual outfit for a summer event, budget $250" transcript={fashionTranscript} data-id="element-2973" />
            <ChatWindow title="Home Appliances (Dishwasher)" subtitle='Quiet, 24" dishwasher with 3rd rack under $700' transcript={dwTranscript} data-id="element-2974" />
          </div>
        </div>
      </section>

      {/* Analytics & Leads */}
      <section className="py-16 bg-gray-50 border-t border-b border-gray-200" data-id="element-2975">
        <div className="max-w-6xl mx-auto px-6" data-id="element-2976">
          <h2 className="text-3xl font-bold text-center mb-6" data-id="element-2977">
            Analytics that Drive Revenue
          </h2>
          <p className="text-center text-gray-600 max-w-3xl mx-auto mb-10" data-id="element-2978">
            Understand what shoppers are asking, what they buy, and who they are
            — all from your GPT Store conversations. Hook it to your cart in
            minutes.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-10" data-id="element-2979">
            <InfoTile icon={Search} title="What people search" items={["Top intents: '4K TV for bright room', 'quiet dishwasher < $700', 'black sneakers EU42'.", 'Missed intents & content gaps flagged automatically.']} data-id="element-2980" />
            <InfoTile icon={ShoppingCart} title="What they buy" items={['Products added‑to‑cart & purchased via your site.', 'Attribution to GPT conversations and prompts.']} data-id="element-2981" />
            <InfoTile icon={Mail} title="Leads you capture" items={['Name, phone, and email of engaged shoppers (opt‑in).', 'Conversation transcripts for CRM follow‑up.']} data-id="element-2982" />
          </div>
          <div className="grid lg:grid-cols-2 gap-8 items-start" data-id="element-2983">
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-card" data-id="element-2984">
              <div className="flex items-center gap-2 mb-4" data-id="element-2985">
                <BarChart3 className="h-5 w-5 text-primary" data-id="element-2986" />
                <span className="font-semibold" data-id="element-2987">Engagement Dashboard</span>
              </div>
              {/* Dashboard visualization instead of placeholder */}
              <div className="rounded-xl border border-gray-200 overflow-hidden" data-id="element-2988">
                <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex justify-between items-center" data-id="element-2989">
                  <div className="font-medium text-sm" data-id="element-2990">
                    Conversation Analytics
                  </div>
                  <div className="flex items-center gap-2" data-id="element-2991">
                    <span className="text-xs text-gray-500" data-id="element-2992">Last 30 days</span>
                    <button className="text-xs bg-white border border-gray-200 rounded px-2 py-1 flex items-center" data-id="element-2993">
                      <BarChart2Icon className="h-3 w-3 mr-1" data-id="element-2994" />
                      Export
                    </button>
                  </div>
                </div>
                <div className="p-4" data-id="element-2995">
                  <div className="grid grid-cols-4 gap-3 mb-4" data-id="element-2996">
                    <div className="bg-gray-50 rounded p-2" data-id="element-2997">
                      <div className="text-xs text-gray-500" data-id="element-2998">Conversations</div>
                      <div className="text-lg font-bold" data-id="element-2999">28,104</div>
                    </div>
                    <div className="bg-gray-50 rounded p-2" data-id="element-3000">
                      <div className="text-xs text-gray-500" data-id="element-3001">Avg. Duration</div>
                      <div className="text-lg font-bold" data-id="element-3002">4.2 min</div>
                    </div>
                    <div className="bg-gray-50 rounded p-2" data-id="element-3003">
                      <div className="text-xs text-gray-500" data-id="element-3004">Conversion</div>
                      <div className="text-lg font-bold" data-id="element-3005">7.9%</div>
                    </div>
                    <div className="bg-gray-50 rounded p-2" data-id="element-3006">
                      <div className="text-xs text-gray-500" data-id="element-3007">AOV</div>
                      <div className="text-lg font-bold" data-id="element-3008">$194</div>
                    </div>
                  </div>
                  <div className="mb-4" data-id="element-3009">
                    <div className="text-sm font-medium mb-2" data-id="element-3010">Top Intents</div>
                    <div className="space-y-2" data-id="element-3011">
                      {['TV for bright room', 'Dishwasher under $700', 'Sneakers EU42', 'iPhone trade-in'].map((intent, i) => <div key={i} className="flex justify-between items-center" data-id="element-3012">
                          <div className="text-xs" data-id="element-3013">{intent}</div>
                          <div className="w-1/2 bg-gray-100 rounded-full h-2" data-id="element-3014">
                            <div className="bg-blue-500 h-2 rounded-full" style={{
                          width: `${80 - i * 15}%`
                        }} data-id="element-3015"></div>
                          </div>
                        </div>)}
                    </div>
                  </div>
                  <div data-id="element-3016">
                    <div className="text-sm font-medium mb-2" data-id="element-3017">
                      Conversion Funnel
                    </div>
                    <div className="flex items-end h-20 gap-1" data-id="element-3018">
                      <div className="h-full bg-blue-500 rounded-t w-1/4 flex items-end justify-center" data-id="element-3019">
                        <span className="text-white text-xs pb-1" data-id="element-3020">100%</span>
                      </div>
                      <div className="h-[60%] bg-indigo-500 rounded-t w-1/4 flex items-end justify-center" data-id="element-3021">
                        <span className="text-white text-xs pb-1" data-id="element-3022">60%</span>
                      </div>
                      <div className="h-[8%] bg-purple-500 rounded-t w-1/4 flex items-end justify-center" data-id="element-3023">
                        <span className="text-white text-xs pb-1" data-id="element-3024">8%</span>
                      </div>
                      <div className="h-[3%] bg-green-500 rounded-t w-1/4 flex items-end justify-center" data-id="element-3025">
                        <span className="text-white text-xs pb-1" data-id="element-3026">3%</span>
                      </div>
                    </div>
                    <div className="flex text-xs text-gray-500 mt-1" data-id="element-3027">
                      <div className="w-1/4 text-center" data-id="element-3028">Chat</div>
                      <div className="w-1/4 text-center" data-id="element-3029">View</div>
                      <div className="w-1/4 text-center" data-id="element-3030">Cart</div>
                      <div className="w-1/4 text-center" data-id="element-3031">Buy</div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3" data-id="element-3032">
                Powered by Shopilots analytics; retrieval & chat signals
                stitched to revenue.
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-card" data-id="element-3033">
              <div className="flex items-center gap-2 mb-4" data-id="element-3034">
                <LineChart className="h-5 w-5 text-primary" data-id="element-3035" />
                <span className="font-semibold" data-id="element-3036">Cart Connection</span>
              </div>
              {/* Cart Connection visualization */}
              <div className="rounded-xl border border-gray-200 overflow-hidden mb-4" data-id="element-3037">
                <div className="bg-gray-50 border-b border-gray-200 px-4 py-3" data-id="element-3038">
                  <div className="font-medium text-sm" data-id="element-3039">Integration Flow</div>
                </div>
                <div className="p-4" data-id="element-3040">
                  <div className="flex items-center justify-between mb-6" data-id="element-3041">
                    <div className="flex-shrink-0 w-24 h-16 border border-gray-200 rounded-lg flex items-center justify-center bg-blue-50" data-id="element-3042">
                      <div className="text-center" data-id="element-3043">
                        <Bot className="h-6 w-6 text-blue-500 mx-auto" data-id="element-3044" />
                        <div className="text-xs mt-1" data-id="element-3045">GPT Store</div>
                      </div>
                    </div>
                    <div className="flex-grow flex items-center justify-center" data-id="element-3046">
                      <div className="h-0.5 w-full bg-gray-200 relative" data-id="element-3047">
                        <ArrowRightIcon className="h-4 w-4 text-gray-400 absolute top-1/2 right-0 transform -translate-y-1/2" data-id="element-3048" />
                      </div>
                    </div>
                    <div className="flex-shrink-0 w-24 h-16 border border-gray-200 rounded-lg flex items-center justify-center bg-purple-50" data-id="element-3049">
                      <div className="text-center" data-id="element-3050">
                        <CreditCardIcon className="h-6 w-6 text-purple-500 mx-auto" data-id="element-3051" />
                        <div className="text-xs mt-1" data-id="element-3052">Your Cart</div>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3" data-id="element-3053">
                    <div className="border border-gray-200 rounded p-2 flex items-center gap-2" data-id="element-3054">
                      <BoxIcon className="h-4 w-4 text-gray-500" data-id="element-3055" />
                      <div className="text-xs" data-id="element-3056">Product IDs</div>
                    </div>
                    <div className="border border-gray-200 rounded p-2 flex items-center gap-2" data-id="element-3057">
                      <TagIcon className="h-4 w-4 text-gray-500" data-id="element-3058" />
                      <div className="text-xs" data-id="element-3059">Price & Quantity</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3" data-id="element-3060">
                    <div className="border border-gray-200 rounded p-2 flex items-center gap-2" data-id="element-3061">
                      <RefreshCwIcon className="h-4 w-4 text-gray-500" data-id="element-3062" />
                      <div className="text-xs" data-id="element-3063">Session Data</div>
                    </div>
                    <div className="border border-gray-200 rounded p-2 flex items-center gap-2" data-id="element-3064">
                      <ActivityIcon className="h-4 w-4 text-gray-500" data-id="element-3065" />
                      <div className="text-xs" data-id="element-3066">Order Analytics</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3" data-id="element-3067">
                <div className="rounded-xl border border-gray-200 p-3 text-xs" data-id="element-3068">
                  <span className="block font-semibold mb-1" data-id="element-3069">
                    Supported now
                  </span>
                  <div className="flex flex-wrap gap-1" data-id="element-3070">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200" data-id="element-3071">
                      Shopify
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-purple-50 text-purple-700 border border-purple-200" data-id="element-3072">
                      WooCommerce
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-200" data-id="element-3073">
                      Custom API
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-orange-50 text-orange-700 border border-orange-200" data-id="element-3074">
                      SFCC*
                    </span>
                  </div>
                </div>
                <div className="rounded-xl border border-gray-200 p-3 text-xs" data-id="element-3075">
                  <span className="block font-semibold mb-1" data-id="element-3076">Data sync</span>
                  <div className="space-y-1" data-id="element-3077">
                    <div className="flex items-center gap-1" data-id="element-3078">
                      <CheckIcon className="h-3 w-3 text-green-500" data-id="element-3079" />
                      <span data-id="element-3080">Products & inventory</span>
                    </div>
                    <div className="flex items-center gap-1" data-id="element-3081">
                      <CheckIcon className="h-3 w-3 text-green-500" data-id="element-3082" />
                      <span data-id="element-3083">Prices & promotions</span>
                    </div>
                    <div className="flex items-center gap-1" data-id="element-3084">
                      <CheckIcon className="h-3 w-3 text-green-500" data-id="element-3085" />
                      <span data-id="element-3086">Images & attributes</span>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3" data-id="element-3087">
                *Salesforce Commerce Cloud via connector. Contact us for others.
              </p>
            </div>
          </div>
          <div className="text-center mt-10" data-id="element-3088">
            <Link to="/gpt-store" data-id="element-3089">
              <Button variant="outline" size="lg" data-id="element-3090">
                See Your Brand Inside GPT - No Credit Card Required
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 text-center bg-gradient-to-r from-slate-900 to-slate-800 text-white" data-id="element-3091">
        <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-id="element-3092">
          See Your GPT Store in Action
        </h2>
        <p className="max-w-2xl mx-auto text-slate-300 mb-8" data-id="element-3093">
          Share your website with us today. Within{' '}
          <span className="font-semibold" data-id="element-3094">6 hours</span>, we'll plug your
          catalog into a GPT demo so you can experience how your customers will
          shop tomorrow.
        </p>
        <Button variant="primary" size="lg" className="bg-white text-slate-900 hover:bg-slate-200" onClick={() => setIsWizardOpen(true)} data-id="element-3095">
          Submit Your Website →
        </Button>
      </section>

      {/* Wizard Modal */}
      <Wizard isOpen={isWizardOpen} onClose={() => setIsWizardOpen(false)} data-id="element-3096" />
    </div>;
}